/**
 * Canal de présence « en direct » (RGPD, contrat détaillé dans index.tsx) —
 * démarré indépendamment du reste du widget, avant tout clic du visiteur.
 * Minimal par construction :
 *   - aucun cookie, aucun localStorage ;
 *   - un seul identifiant, un UUID aléatoire en sessionStorage (clé
 *     `bokame_presence`), qui meurt avec l'onglet — sert uniquement à relier
 *     les pages d'une même session dans le même onglet, jamais entre visites ;
 *   - les seules données envoyées : cet id, l'URL courante (sans query ni
 *     fragment, tronquée), le titre de la page, le referrer (hostname
 *     uniquement, envoyé une seule fois au premier `enter`) et la langue du
 *     navigateur. Aucune n'est écrite en base côté serveur (store mémoire
 *     avec expiration — voir backend/src/presence/store.ts).
 * Silencieux par construction : ne doit jamais lever ni logger en prod — une
 * panne de ce canal ne doit jamais affecter le reste du site.
 */

const PRESENCE_STORAGE_KEY = 'bokame_presence';
const PING_INTERVAL_MS = 30_000;
const URL_MAX_LEN = 300;
const TITLE_MAX_LEN = 200;

type PresenceCtx = {
  backendUrl: string;
  /**
   * Bloc d'identité signé HMAC (même token que celui utilisé par
   * `/visitor/identify`), présent dans `window.bokameChatCtx` quand le
   * visiteur est connecté à WordPress/WooCommerce. Transmis tel quel avec
   * l'événement `enter` uniquement — jamais avec `ping`/`leave` — le backend
   * vérifie la signature (voir `src/api/presence.ts`).
   */
  identityToken?: string;
};

type PresenceEvent = 'enter' | 'ping' | 'leave';

let presenceId: string | null = null;
let ctxRef: PresenceCtx | null = null;
let started = false;
let pingTimerId: number | null = null;
let lastUrl = '';
let historyPatched = false;

function generateId(): string {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  // Fallback pour les navigateurs sans crypto.randomUUID — qualité UUID v4
  // suffisante ici (identifiant local, pas de garantie cryptographique requise).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Lit/crée l'identifiant de présence en sessionStorage. Jamais un cookie. */
export function getPresenceId(): string {
  if (presenceId) return presenceId;
  try {
    const existing = sessionStorage.getItem(PRESENCE_STORAGE_KEY);
    if (existing) {
      presenceId = existing;
      return existing;
    }
  } catch {
    /* sessionStorage indisponible (navigation privée stricte, etc.) */
  }
  const id = generateId();
  presenceId = id;
  try {
    sessionStorage.setItem(PRESENCE_STORAGE_KEY, id);
  } catch {
    /* tant pis — l'id reste valable en mémoire pour cette page */
  }
  return id;
}

function currentUrl(): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}`.slice(0, URL_MAX_LEN);
}

function hostnameOf(url: string): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).hostname || undefined;
  } catch {
    return undefined;
  }
}

function send(event: PresenceEvent, extra: { referrerHost?: string } = {}): void {
  if (!ctxRef) return;
  const payload: Record<string, unknown> = {
    id: getPresenceId(),
    event,
    url: currentUrl(),
    title: (document.title || '').slice(0, TITLE_MAX_LEN),
    locale: navigator.language,
    ...extra,
  };
  // Identité : uniquement sur `enter`, tel quel — la vérification de
  // signature se fait côté serveur (voir PresenceCtx ci-dessus).
  if (event === 'enter' && ctxRef.identityToken) {
    payload.identityToken = ctxRef.identityToken;
  }
  const url = `${ctxRef.backendUrl}/api/visitor/presence`;
  try {
    if (event === 'leave' && typeof navigator.sendBeacon === 'function') {
      // `text/plain` reste dans les types "simples" CORS — pas de preflight,
      // et sendBeacon fonctionne même pendant la décharge de la page.
      navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'text/plain' }));
      return;
    }
    void fetch(url, {
      method: 'POST',
      credentials: 'omit',
      mode: 'cors',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {
    /* canal best-effort — ne doit jamais faire échouer le reste du site */
  }
}

function clearPingTimer(): void {
  if (pingTimerId !== null) {
    window.clearInterval(pingTimerId);
    pingTimerId = null;
  }
}

function schedulePing(): void {
  clearPingTimer();
  pingTimerId = window.setInterval(() => {
    if (document.visibilityState === 'visible') send('ping');
  }, PING_INTERVAL_MS);
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') send('ping');
}

function onUrlChange(): void {
  const url = currentUrl();
  if (url === lastUrl) return;
  lastUrl = url;
  send('enter');
}

function onPageHide(): void {
  send('leave');
}

/**
 * Démarre le canal de présence pour la page courante. Idempotent — un seul
 * `enter`/timer par chargement de page, appelé une fois depuis index.tsx.
 */
export function startPresence(ctx: PresenceCtx): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  ctxRef = ctx;
  lastUrl = currentUrl();

  const referrerHost = hostnameOf(document.referrer);
  send('enter', referrerHost !== undefined ? { referrerHost } : {});
  schedulePing();

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('popstate', onUrlChange);
  window.addEventListener('pagehide', onPageHide);

  // Monkey-patch léger pour détecter les navigations via history.pushState
  // (SPA-like). bokame.com est du WordPress classique donc rarement
  // déclenché, mais couvre le cas sans coût.
  if (!historyPatched && window.history?.pushState) {
    historyPatched = true;
    const originalPushState = window.history.pushState.bind(window.history);
    window.history.pushState = function patchedPushState(
      ...args: Parameters<History['pushState']>
    ): void {
      originalPushState(...args);
      onUrlChange();
    };
  }
}
